import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { IoMdMegaphone } from 'react-icons/io';
import { FiMail, FiUsers, FiFileText, FiMessageCircle, FiShare2 } from 'react-icons/fi';
import api from '../../../api/api';
import { IGlobalState } from '../../../model/IGlobalState';
import { CommunicationsCountsResult } from '../../../model/CommunicationsCountsResult';
import Announcements from './Announcements';
import EmailCampaigns from './EmailCampaigns';
import InvestorQuestions from './InvestorQuestions';
import PublishedAnswers from './PublishedAnswers';
import EmailTemplates from './EmailTemplates';
import TemplatePreview from './TemplatePreview';
import TemplateBuilder from './TemplateBuilder';
import NewTemplate from './NewTemplate';
import AnnouncementEmailPreview, { AnnouncementView } from './AnnouncementEmailPreview';
import NewCampaign from './NewCampaign';
import EmailBuilder from './EmailBuilder';
import CampaignDetails from './CampaignDetails';
import Contacts from './contacts/Contacts';
import ContactDetail from './contacts/ContactDetail';
import ImportContacts from './contacts/ImportContacts';
import SocialPosts from './SocialPosts';

// url segment → tab; no segment = Announcements. Social Posts has no count (no post history source), so no badge.
const tabs = [
    { path: '', label: 'Announcements', icon: <IoMdMegaphone />, count: (c: CommunicationsCountsResult) => c.announcements, component: Announcements },
    { path: 'email-campaigns', label: 'Email Campaigns', icon: <FiMail />, count: (c: CommunicationsCountsResult) => c.emailCampaigns, component: EmailCampaigns },
    { path: 'contacts', label: 'Contacts', icon: <FiUsers />, count: (c: CommunicationsCountsResult) => c.contacts, component: Contacts },
    { path: 'email-templates', label: 'Email Templates', icon: <FiFileText />, count: (c: CommunicationsCountsResult) => c.emailTemplates, component: EmailTemplates },
    { path: 'social-posts', label: 'Social Posts', icon: <FiShare2 />, component: SocialPosts },
    { path: 'investor-questions', label: 'Investor Questions', icon: <FiMessageCircle />, count: (c: CommunicationsCountsResult) => c.investorQuestions, component: InvestorQuestions },
];

const Communications: React.FC = () => {
    const history = useHistory();
    const { tab = '', id, view } = useParams<{ tab?: string; id?: string; view?: string }>();
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const [counts, setCounts] = useState<CommunicationsCountsResult | null>(null);
    // /communications/campaign/new, /campaign/{id} (builder, drafts) and /campaign/{id}/details|preview|statistics sit under Email Campaigns
    const current = tabs.find(x => x.path === (tab === 'campaign' ? 'email-campaigns' : tab)) ?? tabs[0];

    const loadCounts = () => { if (stock?.id) api().getCommunicationsCounts(stock.id).then(setCounts).catch(() => setCounts(null)); };

    useEffect(() => {
        setCounts(null);
        loadCounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stock?.id]);

    // tablet / mobile: the tab row scrolls sideways (Page.scss), so bring the active tab into view (same as Client settings)
    const tabsRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const row = tabsRef.current;
        const active = row?.querySelector<HTMLElement>('button.active');
        if (!row || !active || row.scrollWidth <= row.clientWidth) return;
        row.scrollTo({ left: active.offsetLeft - (row.clientWidth - active.offsetWidth) / 2, behavior: 'smooth' });
    }, [current]);

    return <div className="v2-page communications-v2">
        <div className="sub-tabs" ref={tabsRef}>
            {tabs.map(x => <button key={x.path} type="button" className={x === current ? 'active' : ''}
                onClick={() => history.push(`/communications${x.path && `/${x.path}`}`)}>
                {x.icon} {x.label}
                {counts && x.count && <span className="badge">{x.count(counts).toLocaleString()}</span>}
            </button>)}
        </div>

        {tab === 'announcement' && id
            ? <AnnouncementEmailPreview sendGuid={id} view={(view || 'details') as AnnouncementView} onChanged={loadCounts} />
            : tab === 'campaign' && id
                ? id === 'new' ? <NewCampaign />
                    : view === 'details' || view === 'preview' || view === 'statistics' ? <CampaignDetails campaignId={decodeURIComponent(id)} view={view} />
                    : <EmailBuilder campaignId={decodeURIComponent(id)} />
                // /communications/contacts/import, /contacts/{email}[/activity]
                : tab === 'contacts' && id
                    ? id === 'import' ? <ImportContacts /> : <ContactDetail contactKey={id} view={view} />
                : tab === 'email-templates' && id
                    ? id === 'new' ? <NewTemplate onChanged={loadCounts} />
                        : view === 'edit' ? <TemplateBuilder templateId={decodeURIComponent(id)} /> : <TemplatePreview templateId={decodeURIComponent(id)} />
                    // /communications/investor-questions/published; any other id is a question guid (moderator email link)
                    : tab === 'investor-questions' && id === 'published' ? <PublishedAnswers />
                    : <current.component onChanged={loadCounts} />}
    </div>;
};

export default Communications;
